/**
 * App Tracking Transparency (ATT) bridge.
 *
 * Apple Guideline 5.1.2(i) requires the native app to request ATT permission
 * before any advertising/tracking signals are set. Our GTM Consent Mode grants
 * `ad_*` (remarketing) signals, which count as tracking — so on the iOS shell
 * those signals must be gated behind ATT.
 *
 * This talks to a custom native Capacitor plugin (`AppTracking`, implemented in
 * `apps/ios/ios/App/App/AppTrackingPlugin.swift`) that wraps
 * `ATTrackingManager`. On every non-iOS platform (web, installed PWA, Android)
 * there is no ATT concept, so the helpers resolve to `authorized` and the
 * advertising gate is effectively a no-op — web/Android behavior is unchanged.
 */
import { registerPlugin } from '@capacitor/core'
import { isCapacitorIOS } from './runtime'

/** Mirrors `ATTrackingManager.AuthorizationStatus`. */
export type TrackingStatus =
  | 'notDetermined'
  | 'restricted'
  | 'denied'
  | 'authorized'

interface AppTrackingPlugin {
  /**
   * Show Apple's ATT prompt if the status is `notDetermined`, otherwise return
   * the existing status immediately. Resolves once the user has responded.
   */
  requestPermission(): Promise<{ status: TrackingStatus }>
  /** Current status without prompting. */
  getStatus(): Promise<{ status: TrackingStatus }>
}

const AppTracking = registerPlugin<AppTrackingPlugin>('AppTracking')

/**
 * Resolve once the webview is foreground-visible. iOS silently declines to
 * present the ATT dialog (returning `notDetermined`) when the request arrives
 * before the app is active — e.g. a cold launch into the background — so the
 * launch-time request must wait. In WKWebView `document.visibilityState`
 * tracks the app's foreground state, so no native bridge call is needed. If
 * the dialog still fails to present, the status stays `notDetermined` and the
 * next launch retries — ads simply stay denied for this session (fail closed).
 */
async function waitForAppActive(): Promise<void> {
  if (document.visibilityState === 'visible') return
  await new Promise<void>((resolve) => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      document.removeEventListener('visibilitychange', onVisible)
      resolve()
    }
    document.addEventListener('visibilitychange', onVisible)
    // The state can flip to visible between the guard above and the listener
    // attaching — re-check so that transition isn't silently missed.
    onVisible()
  })
}

/**
 * Request ATT authorization (native iOS only) and report whether advertising
 * signals may be granted. Non-iOS platforms return `true` unconditionally.
 *
 * Called at every iOS launch (see `applyIosLaunchConsent` in `consent.ts`):
 * the system dialog is shown only while the status is `notDetermined`, so
 * later launches resolve immediately with the user's standing answer.
 */
export async function requestAdTrackingConsent(): Promise<boolean> {
  if (!isCapacitorIOS()) return true
  try {
    await waitForAppActive()
    const { status } = await AppTracking.requestPermission()
    return status === 'authorized'
  } catch {
    // Plugin missing (older build) or the request failed — fail closed: no ATT
    // authorization means no advertising signals.
    return false
  }
}

/**
 * Whether advertising signals are currently permitted, without prompting.
 * Non-iOS platforms return `true`. Used when re-applying a stored consent
 * choice on launch — we must not grant `ad_*` on iOS unless ATT is still
 * authorized (the user can revoke it in iOS Settings between launches).
 */
export async function hasAdTrackingConsent(): Promise<boolean> {
  if (!isCapacitorIOS()) return true
  try {
    const { status } = await AppTracking.getStatus()
    return status === 'authorized'
  } catch {
    return false
  }
}
